// The one dialog of the admin app: a native modal <dialog> (the rest of the page goes inert, with
// a backdrop), Tab cycling kept inside it, Esc closes, and focus returns to what opened it.
// ConfirmDialog adds the typed-name confirmation destructive actions need (SCR-004). The help
// modal (ui/help.tsx) is a Dialog too.
import type { ComponentChildren } from "preact";
import { useEffect, useId, useRef, useState } from "preact/hooks";
import { typedNameMatches } from "./forms";
import { Icon } from "./icons";
import { FOCUSABLE } from "./row-click";

export interface DialogProps {
  title: string;
  open: boolean;
  onClose(): void;
  children: ComponentChildren;
  /** Buttons row; the dialog adds nothing of its own so each use states its actions. */
  actions: ComponentChildren;
  /** Extra class on the <dialog> (e.g. `dialog-wide`, `help-dialog`). */
  class?: string;
  /** A close (×) button in the corner, named by this label. */
  closeLabel?: string;
}

export function Dialog({ title, open, onClose, children, actions, class: className, closeLabel }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.showModal();
    return () => {
      dialog.close();
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  const trapTab = (event: KeyboardEvent) => {
    if (event.key !== "Tab" || !ref.current) return;
    const focusable = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;
  return (
    <dialog
      ref={ref}
      class={className ? `dialog ${className}` : "dialog"}
      aria-labelledby={titleId}
      onKeyDown={trapTab}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // A click on the backdrop (the dialog element itself, outside its box) closes it.
        const dialog = ref.current;
        if (!dialog || event.target !== dialog) return;
        const box = dialog.getBoundingClientRect();
        const inside = event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
        if (!inside) onClose();
      }}
    >
      <div class="dialog-head">
        <h2 id={titleId} class="dialog-title">
          {title}
        </h2>
        {closeLabel && (
          <button type="button" class="icon-button" aria-label={closeLabel} onClick={onClose}>
            <Icon name="close" />
          </button>
        )}
      </div>
      <div class="dialog-body">{children}</div>
      {actions && <div class="dialog-actions">{actions}</div>}
    </dialog>
  );
}

export interface ConfirmDialogProps {
  title: string;
  open: boolean;
  /** What will happen, in plain words. */
  message: ComponentChildren;
  confirmLabel: string;
  /** When set, the confirm button waits until this exact name is typed. */
  typedName?: string;
  busy?: boolean;
  onConfirm(): void;
  onClose(): void;
}

export function ConfirmDialog(props: ConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  const inputId = useId();
  const ready = props.typedName === undefined || typedNameMatches(props.typedName, typed);
  return (
    <Dialog
      title={props.title}
      open={props.open}
      onClose={props.onClose}
      actions={
        <>
          <button type="button" class="button button-secondary" onClick={props.onClose}>
            Cancel
          </button>
          <button type="button" class="button button-danger" disabled={!ready || props.busy} onClick={props.onConfirm}>
            {props.busy ? "Working…" : props.confirmLabel}
          </button>
        </>
      }
    >
      <div class="dialog-message">{props.message}</div>
      {props.typedName !== undefined && (
        <div class="field">
          {/* help-exempt: the typed-name confirmation explains itself. */}
          <label for={inputId}>
            Type <strong>{props.typedName}</strong> to confirm
          </label>
          <input
            id={inputId}
            type="text"
            autocomplete="off"
            spellcheck={false}
            value={typed}
            onInput={(event) => setTyped(event.currentTarget.value)}
          />
        </div>
      )}
    </Dialog>
  );
}
